import { FreetextSuggestion } from '@/types/admin'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@brent/core-ui/ui'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@brent/core-ui/ui'
import { Badge } from '@brent/core-ui/ui'
import { Button } from '@brent/core-ui/ui'
import { format } from 'date-fns'

interface FreetextTableProps {
  suggestions: FreetextSuggestion[]
  onResolve: (suggestion: FreetextSuggestion) => void
  showResolved?: boolean
  selectedIds?: Set<string>
  onSelectionChange?: (selectedIds: Set<string>) => void
  selectionMode?: boolean
}

export function FreetextTable({
  suggestions,
  onResolve,
  showResolved = false,
  selectedIds = new Set(),
  onSelectionChange,
  selectionMode = false,
}: FreetextTableProps) {
  const handleRowClick = (suggestionId: string) => {
    if (!selectionMode || !onSelectionChange) return

    const newSelected = new Set(selectedIds)
    if (newSelected.has(suggestionId)) {
      newSelected.delete(suggestionId)
    } else {
      // Only allow selecting 2 items max
      if (newSelected.size >= 2) {
        return
      }
      newSelected.add(suggestionId)
    }
    onSelectionChange(newSelected)
  }

  if (suggestions.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>
            {showResolved ? 'Resolved Freetext Entries' : 'Unresolved Freetext Entries'}
          </CardTitle>
          <CardDescription>
            {showResolved
              ? 'No resolved freetext entries found'
              : 'No unresolved freetext entries found'}
          </CardDescription>
        </CardHeader>
      </Card>
    )
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>
          {showResolved ? 'Resolved Freetext Entries' : 'Unresolved Freetext Entries'}
        </CardTitle>
        <CardDescription>
          {showResolved
            ? `${suggestions.length} resolved custom ${suggestions.length === 1 ? 'entry' : 'entries'}`
            : `${suggestions.length} custom ${suggestions.length === 1 ? 'entry' : 'entries'} awaiting resolution`}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Member</TableHead>
              <TableHead>Composer</TableHead>
              <TableHead>Piece</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Created</TableHead>
              {showResolved && <TableHead>Resolved</TableHead>}
              {!showResolved && <TableHead>Actions</TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {suggestions.map((suggestion) => {
              const isSelected = selectedIds.has(suggestion.suggestion_id)
              return (
                <TableRow
                  key={suggestion.suggestion_id}
                  className={`${selectionMode ? 'cursor-pointer' : ''} ${
                    isSelected ? 'bg-primary/10' : ''
                  }`}
                  onClick={() => handleRowClick(suggestion.suggestion_id)}
                >
                  <TableCell>
                    <div className="flex flex-col">
                      <span className="font-medium">{suggestion.user_name || 'Unknown'}</span>
                      <span className="text-sm text-muted-foreground">{suggestion.user_email}</span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <span>{suggestion.composer}</span>
                      {suggestion.is_composer_freetext && (
                        <Badge
                          variant="outline"
                          className="bg-yellow-50 text-yellow-800 border-yellow-200"
                        >
                          Custom
                        </Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <span>{suggestion.title}</span>
                      {suggestion.is_work_freetext && (
                        <Badge
                          variant="outline"
                          className="bg-yellow-50 text-yellow-800 border-yellow-200"
                        >
                          Custom
                        </Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    {suggestion.is_composer_freetext && suggestion.is_work_freetext && (
                      <Badge variant="secondary">Both</Badge>
                    )}
                    {suggestion.is_composer_freetext && !suggestion.is_work_freetext && (
                      <Badge variant="secondary">Composer</Badge>
                    )}
                    {!suggestion.is_composer_freetext && suggestion.is_work_freetext && (
                      <Badge variant="secondary">Piece</Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {format(new Date(suggestion.created_at), 'PP')}
                  </TableCell>
                  {showResolved && suggestion.resolved_at && (
                    <TableCell className="text-sm text-muted-foreground">
                      {format(new Date(suggestion.resolved_at), 'PP')}
                    </TableCell>
                  )}
                  {!showResolved && (
                    <TableCell>
                      <Button
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation() // Prevent row selection when clicking button
                          onResolve(suggestion)
                        }}
                        disabled={selectionMode}
                      >
                        Resolve
                      </Button>
                    </TableCell>
                  )}
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  )
}
